import { useEffect } from "react";
import { PLATFORM_NAME } from "../../../features/tenantDefaults";
import { FUNKCIE } from "./Funkcie";

/**
 * Hlavicka dokumentu marketingovej stranky (GEO): title, description,
 * canonical, Open Graph a strukturovane data (JSON-LD).
 *
 * Nastavuje sa tu, nie v public/index.html -- ten isty index.html sluzi aj
 * prevadzkam (rozvozy.sk/<slug>) a /sprava, kde marketingove meta nepatria.
 * Do statickeho HTML pre AI crawlery sa dostane cez scripts/prerender.js.
 *
 * FAQPage sa sklada z tych istych otazok, ktore su na stranke -- Google aj
 * AI vyhladavace odmietaju strukturovane data, ktore nesedia s viditelnym textom.
 */
export const SITE_URL = "https://rozvozy.sk";

export const LANDING_TITLE = `${PLATFORM_NAME} – systém na rozvoz obedov pre reštaurácie a jedálne`;

export const LANDING_DESCRIPTION =
  "Program na rozvoz obedov pre malé reštaurácie a jedálne: denné menu, objednávky telefonicky aj cez web, " +
  "trasy vodičov a evidencia platieb pri doručení.";

// Slova, ktorymi sa ludia na system pytaju (overene v Perplexity a Gemini) --
// AI vyhladavanie hlada podla slov, nie podla vyznamu stranky.
const KLUCOVE_SLOVA = [
  "systém na rozvoz obedov",
  "program na evidenciu rozvozov",
  "evidencia objednávok obedov",
  "aplikácia pre vodičov rozvozu",
  "softvér pre jedálne a reštaurácie",
  "náhrada zošita a Excelu s objednávkami",
];

// Rozmery musia sediet so suborom v public/ -- siete podla nich skladaju kartu.
export const LANDING_IMAGE = {
  url: `${SITE_URL}/rozvozy_icon2.png`,
  width: 998,
  height: 937,
  alt: "Logo Rozvozy – písmeno R s poklopom na kolieskach",
};

// Akcia: zdarma len pre prvych 10 zaujemcov a len docasne. Ta ista veta je
// v JSON-LD, v pase pred formularom a (rozvedena) v hero, FAQ a public/llms.txt.
export const PONUKA_ZDARMA = "Pre prvých 10 záujemcov sú Rozvozy dočasne zdarma.";

// Obchodny kontakt (sekcia Kontakt). Ta ista adresa je vo VOP a v GDPR.
export const KONTAKT_EMAIL = "admin@rozvozy.sk";

export const SIDLO = { ulica: "Hutnícka 1", psc: "040 01", mesto: "Košice" };

const PREVADZKOVATEL = {
  name: "Inver s.r.o.",
  legalName: "INVER spol. s r.o.",
  url: "https://www.inver.sk/",
  email: KONTAKT_EMAIL,
  address: {
    "@type": "PostalAddress",
    streetAddress: SIDLO.ulica,
    postalCode: SIDLO.psc,
    addressLocality: SIDLO.mesto,
    addressCountry: "SK",
  },
  contactPoint: [{ "@type": "ContactPoint", contactType: "sales", email: KONTAKT_EMAIL, availableLanguage: "sk" }],
};

export function landingJsonLd(otazky) {
  const org = `${SITE_URL}/#prevadzkovatel`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "Organization", "@id": org, ...PREVADZKOVATEL },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#web`,
        url: `${SITE_URL}/`,
        name: PLATFORM_NAME,
        inLanguage: "sk",
        publisher: { "@id": org },
      },
      {
        "@type": "SoftwareApplication",
        name: PLATFORM_NAME,
        url: `${SITE_URL}/`,
        image: LANDING_IMAGE.url,
        description: LANDING_DESCRIPTION,
        applicationCategory: "BusinessApplication",
        keywords: KLUCOVE_SLOVA.join(", "),
        operatingSystem: "Web",
        inLanguage: "sk",
        publisher: { "@id": org },
        featureList: FUNKCIE.map(({ title, text }) => `${title}: ${text}`),
        // musi sediet s FAQ "Kolko Rozvozy stoja?" na stranke; LimitedAvailability,
        // lebo zdarma je len pre prvych 10 -- nie bezpodmienecne
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "EUR",
          availability: "https://schema.org/LimitedAvailability",
          description: PONUKA_ZDARMA,
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: otazky.map(({ q, a }) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      },
    ],
  };
}

// Vsetko, co stranka do <head> pridala, nesie data-landing. Predrenderovane
// HTML (scripts/prerender.js) tieto prvky uz obsahuje -- znova sa pouziju,
// inak by po nacitani JS bol JSON-LD aj canonical v hlavicke dvakrat.
// Pri odchode (napr. na /vop) sa odstrania a title/description sa vratia.
const ZNACKA = "data-landing";

function prvok(tag, kluc, hodnota, atributy) {
  let el = document.head.querySelector(`${tag}[${kluc}="${hodnota}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(kluc, hodnota);
    el.setAttribute(ZNACKA, "");
    document.head.appendChild(el);
  }
  Object.entries(atributy).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function LandingHead({ otazky }) {
  useEffect(() => {
    const povodnyTitle = document.title;
    const description = document.head.querySelector('meta[name="description"]');
    const povodnyDescription = description?.getAttribute("content");
    const meta = (attr, key, content) => prvok("meta", attr, key, { content });

    document.title = LANDING_TITLE;
    meta("name", "description", LANDING_DESCRIPTION);
    prvok("link", "rel", "canonical", { href: `${SITE_URL}/` });

    meta("property", "og:type", "website");
    meta("property", "og:locale", "sk_SK");
    meta("property", "og:site_name", PLATFORM_NAME);
    meta("property", "og:title", LANDING_TITLE);
    meta("property", "og:description", LANDING_DESCRIPTION);
    meta("property", "og:url", `${SITE_URL}/`);
    meta("property", "og:image", LANDING_IMAGE.url);
    meta("property", "og:image:width", String(LANDING_IMAGE.width));
    meta("property", "og:image:height", String(LANDING_IMAGE.height));
    meta("property", "og:image:alt", LANDING_IMAGE.alt);
    // "summary" = mala stvorcova karta, sedi k ikonke (nie siroky banner)
    meta("name", "twitter:card", "summary");

    prvok("script", "id", "landing-jsonld", { type: "application/ld+json" }).textContent =
      JSON.stringify(landingJsonLd(otazky));

    return () => {
      // V predrenderovanom HTML je "povodny" title uz ten nas
      document.title = povodnyTitle === LANDING_TITLE ? PLATFORM_NAME : povodnyTitle;
      document.head.querySelectorAll(`[${ZNACKA}]`).forEach((el) => el.remove());
      if (description && povodnyDescription != null) description.setAttribute("content", povodnyDescription);
    };
  }, [otazky]);

  return null;
}

export default LandingHead;
